import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import { Parse } from "../../lib/parse";

function GroupMemberModal({ isOpen, onClose, group, onMembersChanged }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loadMembers = useCallback(async () => {
    if (!group?.objectId) return;
    setIsLoading(true);
    try {
      const result = await Parse.Cloud.run("listGroupMembers", {
        groupId: group.objectId
      });
      setMembers(result.members || []);
    } catch (err) {
      setError(err.message || "Failed to load members.");
    } finally {
      setIsLoading(false);
    }
  }, [group?.objectId]);

  useEffect(() => {
    if (isOpen && group?.objectId) {
      loadMembers();
      setSearchQuery("");
      setSearchResults([]);
      setError("");
    }
  }, [isOpen, group?.objectId, loadMembers]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const result = await Parse.Cloud.run("searchTenantUsers", {
        query: searchQuery.trim()
      });
      // Filter out users already in this group
      const memberIds = new Set(members.map((m) => m.objectId));
      const filtered = (result.users || []).filter(
        (u) => !memberIds.has(u.objectId)
      );
      setSearchResults(filtered);
    } catch (err) {
      setError(err.message || "Search failed.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddUser = async (userId) => {
    setError("");
    try {
      await Parse.Cloud.run("assignUserToCertGroup", {
        userId,
        groupId: group.objectId
      });
      await loadMembers();
      setSearchResults((prev) => prev.filter((u) => u.objectId !== userId));
      if (onMembersChanged) onMembersChanged();
    } catch (err) {
      setError(err.message || "Failed to add user.");
    }
  };

  const handleRemoveUser = async (userId) => {
    setError("");
    try {
      await Parse.Cloud.run("removeUserFromCertGroup", { userId });
      await loadMembers();
      if (onMembersChanged) onMembersChanged();
    } catch (err) {
      setError(err.message || "Failed to remove user.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t("cert-mgmt.group-members-title", {
        defaultValue: "Members — {{name}}",
        name: group?.name || ""
      })}
      onClose={onClose}
      closeIconOnly
      closeLabel={t("close", { defaultValue: "Close" })}
      panelClassName="max-w-lg"
    >
      <div className="space-y-4">
        {/* Search bar */}
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("cert-mgmt.search-users", { defaultValue: "Search users by name or email" })}
            className="flex-1 rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
          />
          <Button onClick={handleSearch} disabled={isSearching}>
            {isSearching
              ? t("loading", { defaultValue: "Loading..." })
              : t("cert-mgmt.search", { defaultValue: "Search" })}
          </Button>
        </div>

        {/* Search results */}
        {searchResults.length > 0 && (
          <div className="rounded-xl border border-app-line bg-white">
            <div className="p-2 text-xs font-semibold uppercase tracking-wide text-app-ink-muted">
              {t("cert-mgmt.search-results", { defaultValue: "Search results" })}
            </div>
            <ul className="max-h-40 overflow-y-auto divide-y divide-app-line">
              {searchResults.map((user) => (
                <li
                  key={user.objectId}
                  className="flex items-center justify-between px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium text-app-ink">{user.Name}</p>
                    <p className="text-xs text-app-ink-muted">{user.Email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    className="h-8 w-8 !p-0"
                    onClick={() => handleAddUser(user.objectId)}
                    aria-label={t("cert-mgmt.add-user", { defaultValue: "Add user" })}
                  >
                    <Icon name="plus" className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <p className="m-0 text-sm text-app-danger">{error}</p>}

        {/* Current members */}
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-app-ink-muted">
            {t("cert-mgmt.current-members", { defaultValue: "Current members" })} ({members.length})
          </h4>
          {isLoading ? (
            <p className="text-sm text-app-ink-muted">
              {t("loading", { defaultValue: "Loading..." })}
            </p>
          ) : members.length === 0 ? (
            <p className="text-sm text-app-ink-muted">
              {t("cert-mgmt.no-members", { defaultValue: "No members in this group." })}
            </p>
          ) : (
            <ul className="max-h-52 overflow-y-auto divide-y divide-app-line rounded-xl border border-app-line bg-white">
              {members.map((member) => (
                <li
                  key={member.objectId}
                  className="flex items-center justify-between px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium text-app-ink">{member.Name}</p>
                    <p className="text-xs text-app-ink-muted">{member.Email}</p>
                  </div>
                  <Button
                    variant="danger"
                    className="h-8 w-8 !p-0"
                    onClick={() => handleRemoveUser(member.objectId)}
                    aria-label={t("cert-mgmt.remove-user", { defaultValue: "Remove user" })}
                  >
                    <Icon name="x" className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="ghost" onClick={onClose}>
            {t("close", { defaultValue: "Close" })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default GroupMemberModal;
